import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SecureLS from "secure-ls";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Logout from "@mui/icons-material/Logout";
import { adminModulesList } from "./adminModules";

const ls = new SecureLS({ encodingType: "aes" });
const COLLAPSED_KEY = "adminSidebarCollapsed";

const getInitialCollapsed = () => {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(COLLAPSED_KEY) === "true";
};

const AdminSidebarItem = ({ collapsed, active, icon: Icon, label, onClick }) => {
  const theme = useTheme();

  const button = (
    <Button
      onClick={onClick}
      fullWidth
      startIcon={<Icon sx={{ fontSize: 20 }} />}
      sx={{
        bgcolor: active ? alpha(theme.palette.primary.main, 0.1) : "transparent",
        borderRadius: 2,
        color: active ? "primary.main" : "text.secondary",
        justifyContent: collapsed ? "center" : "flex-start",
        minHeight: 44,
        minWidth: 0,
        px: collapsed ? 1 : 1.5,
        textTransform: "none",
        "& .MuiButton-startIcon": {
          mr: collapsed ? 0 : 1,
        },
        "&:hover": {
          bgcolor: active
            ? alpha(theme.palette.primary.main, 0.14)
            : alpha(theme.palette.primary.main, 0.06),
          color: "primary.main",
        },
      }}
    >
      {!collapsed && (
        <Typography variant="body2" sx={{ fontWeight: active ? 800 : 600 }} noWrap>
          {label}
        </Typography>
      )}
    </Button>
  );

  return collapsed ? <Tooltip title={label}>{button}</Tooltip> : button;
};

const AdminSidebarLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = ls.get("user");
  const [collapsed, setCollapsed] = useState(getInitialCollapsed);

  useEffect(() => {
    window.localStorage.setItem(COLLAPSED_KEY, collapsed ? "true" : "false");
  }, [collapsed]);

  const activePath = useMemo(() => {
    const exact = adminModulesList.find((item) => item.path === location.pathname);
    return exact?.path || "/admin-dashboard";
  }, [location.pathname]);
  const mainModules = useMemo(
    () => adminModulesList.filter((item) => item.path !== "/admin-dashboard/logs"),
    []
  );
  const adminLogsModule = useMemo(
    () => adminModulesList.find((item) => item.path === "/admin-dashboard/logs"),
    []
  );
  const AdminLogsIcon = adminLogsModule?.icon;

  const handleLogout = () => {
    ls.clear();
    window.location.href = "/login";
  };

  return (
    <Box
      sx={{
        bgcolor: "#f7f9fc",
        display: { xs: "block", md: "flex" },
        minHeight: "100vh",
      }}
    >
      <Paper
        component="aside"
        elevation={0}
        sx={{
          bgcolor: "#fff",
          borderBottom: { xs: "1px solid", md: 0 },
          borderColor: "divider",
          borderRadius: 0,
          borderRight: { md: "1px solid" },
          display: "flex",
          flexDirection: "column",
          gap: 2,
          minHeight: { md: "100vh" },
          p: 1.5,
          position: { md: "sticky" },
          top: 0,
          transition: "width 0.18s ease",
          width: { xs: "100%", md: collapsed ? 84 : 280 },
          zIndex: 5,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
          <Box display="flex" alignItems="center" gap={1.25} minWidth={0}>
            <AdminPanelSettingsIcon sx={{ color: "primary.main", fontSize: 38 }} />
            {!collapsed && (
              <Box minWidth={0}>
                <Typography variant="overline" color="primary" sx={{ fontWeight: 800, lineHeight: 1 }}>
                  Sparkle Admin
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.15 }} noWrap>
                  {user?.firstname ? `${user.firstname} ${user.lastname || ""}`.trim() : "Inhouse Admin"}
                </Typography>
              </Box>
            )}
          </Box>
          <IconButton
            onClick={() => setCollapsed((prev) => !prev)}
            size="small"
            sx={{ display: { xs: "none", md: "inline-flex" } }}
          >
            {collapsed ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </Box>

        {!collapsed && (
          <Box
            sx={{
              bgcolor: alpha("#0958d7", 0.06),
              border: "1px solid",
              borderColor: alpha("#0958d7", 0.14),
              borderRadius: 2,
              p: 1.25,
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              Signed in as
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.25 }} noWrap>
              {user?.email || "No email available"}
            </Typography>
          </Box>
        )}

        <Stack
          spacing={0.5}
          sx={{
            display: { xs: "grid", md: "flex" },
            gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))" },
          }}
        >
          {mainModules.map((item) => (
            <AdminSidebarItem
              key={item.path}
              active={activePath === item.path}
              collapsed={collapsed}
              icon={item.icon}
              label={item.title}
              onClick={() => navigate(item.path)}
            />
          ))}
        </Stack>

        <Box sx={{ display: { xs: "none", md: "block" }, flexGrow: 1 }} />

        {adminLogsModule ? (
          <Button
            variant="outlined"
            color={activePath === adminLogsModule.path ? "primary" : "inherit"}
            onClick={() => navigate(adminLogsModule.path)}
            startIcon={collapsed || !AdminLogsIcon ? null : <AdminLogsIcon fontSize="small" />}
            sx={{
              borderColor:
                activePath === adminLogsModule.path ? "primary.main" : "divider",
              color: activePath === adminLogsModule.path ? "primary.main" : "text.secondary",
              justifyContent: "center",
              minWidth: 0,
              textTransform: "none",
              "&:hover": {
                borderColor: "primary.main",
                color: "primary.main",
              },
            }}
          >
            {collapsed && AdminLogsIcon ? <AdminLogsIcon fontSize="small" /> : "Admin Logs"}
          </Button>
        ) : null}

        <Button
          variant="contained"
          color="secondary"
          onClick={handleLogout}
          startIcon={collapsed ? null : <Logout fontSize="small" />}
          sx={{ minWidth: 0 }}
        >
          {collapsed ? <Logout fontSize="small" /> : "Logout"}
        </Button>
      </Paper>

      <Box component="main" sx={{ flex: 1, minWidth: 0 }}>
        {children}
      </Box>
    </Box>
  );
};

export default AdminSidebarLayout;
